import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {MenuItem, UserType} from '../../models/menu.model';
import {UserService} from '../user/user.service';
import {NavigationEnd, Router} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, firstValueFrom, map, startWith, take} from 'rxjs';
import {OperatorViewsConfigService} from '../operator-views-config/operator-views-config-service';
import {GeneralNotificationsService} from '../general-notifications/general-notifications.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  // === BASE MENU CONFIGURATION ===
  private readonly _menuConfig = signal<MenuItem[]>([
    {
      id: 'SCHEDARIO_CLIENTI',
      label: 'SCHEDARIO CLIENTI',
      isExpanded: false,
      permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT],
      children: [
        {
          id: 'SCHEDARIO_CLIENTI.ANAGRAFICHE_CLIENTI',
          label: 'Anagrafiche Clienti',
          route: '/clienti/anagrafiche',
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
        },
        {
          id: 'SCHEDARIO_CLIENTI.CATEGORIE_CLIENTI',
          label: 'Categorie Clienti',
          route: '/clienti/categorie',
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
        },
      ],
    },
    {
      id: 'GESTIONE_STUDIO',
      label: 'GESTIONE STUDIO',
      isExpanded: false,
      permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT],
      children: [
        {
          id: 'GESTIONE_STUDIO.ANAGRAFICA_STUDIO',
          label: 'Anagrafica Studio',
          route: '/studio/anagrafica',
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
        },
        {
          id: 'GESTIONE_STUDIO.UTENTI_DI_STUDIO',
          label: 'Utenti di Studio',
          route: '/studio/utenti',
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
        },
        {
          id: 'GESTIONE_STUDIO.CONFIGURAZIONE_EMAIL',
          label: 'Configurazione E-Mail',
          route: '/studio/configurazione',
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
        },
        {
          id: 'GESTIONE_STUDIO.SALVATAGGIO_ARCHIVI',
          label: 'Salvataggio Archivi',
          route: '/studio/backup-archivi',
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
        },
        {
          id: 'GESTIONE_STUDIO.LICENZA',
          label: 'Licenza',
          isExpanded: false,
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT],
          children: [
            {
              id: 'GESTIONE_STUDIO.LICENZA.CONTRATTO',
              label: 'Contratto',
              route: '/studio/licenza/contratto',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
            {
              id: 'GESTIONE_STUDIO.LICENZA.CANONE_ANNUO',
              label: 'Canone Annuo',
              route: '/studio/licenza/canone',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
          ],
        },
      ],
    },
    {
      id: 'GESTIONE_MODULI',
      label: 'GESTIONE MODULI',
      isExpanded: false,
      permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT],
      children: [
        {
          id: 'GESTIONE_MODULI.ELABORARE',
          label: 'Elaborare',
          isExpanded: false,
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT],
          children: [
            {
              id: 'GESTIONE_MODULI.ELABORARE.PIANO_DEI_CONTI',
              label: 'Piano dei Conti',
              route: '/moduli/elaborare/conti',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
            {
              id: 'GESTIONE_MODULI.ELABORARE.CREDENZIALI_ACCESSO_ADE',
              label: 'Credenziali di Accesso AdE',
              route: '/moduli/elaborare/credenziali-ade',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
            {
              id: 'GESTIONE_MODULI.ELABORARE.GESTIONALE_DI_STUDIO',
              label: 'Gestionale di Studio',
              route: '/moduli/elaborare/gestionale-studio',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
          ]
        },
        {
          id: 'GESTIONE_MODULI.CONDIVIDERE',
          label: 'Condividere',
          isExpanded: false,
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT],
          children: [
            {
              id: 'GESTIONE_MODULI.CONDIVIDERE.CONDIVISIONI_MULTIPLE',
              label: 'Condivisione Multiple',
              route: '/moduli/condividere/conti',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
            {
              id: 'GESTIONE_MODULI.CONDIVIDERE.GESTIONE_CARTELLE',
              label: 'Gestione Cartelle',
              route: '/moduli/condividere/cartelle',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
            {
              id: 'GESTIONE_MODULI.CONDIVIDERE.TITOLI_DOCUMENTI',
              label: 'Titoli Documenti',
              route: '/moduli/condividere/documenti',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
          ]
        },
        {
          id: 'GESTIONE_MODULI.COMUNICARE',
          label: 'Comunicare',
          isExpanded: false,
          permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT],
          children: [
            {
              id: 'GESTIONE_MODULI.COMUNICARE.CONDIVISIONI_MULTIPLE',
              label: 'Condivisione Multiple',
              route: '/moduli/comunicare/conti',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
            {
              id: 'GESTIONE_MODULI.COMUNICARE.GESTIONE_CARTELLE',
              label: 'Gestione Cartelle',
              route: '/moduli/comunicare/cartelle',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
            {
              id: 'GESTIONE_MODULI.COMUNICARE.TITOLI_DOCUMENTI',
              label: 'Titoli Documenti',
              route: '/moduli/comunicare/documenti',
              permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT]
            },
          ]
        },
      ],
    },
    {
      id: 'VERSIONI_RILASCIO',
      label: 'VERSIONI SOFTWARE',
      route: '/note-di-rilascio',
      permissions: [UserType.CONSULTANT, UserType.COMPANY, UserType.COMPANY_NO_CONSULTANT],
    }
  ]);

  // === COMPUTED per il menu filtrato in base all'utente ===
  private hiddenMenuIds = signal<Set<string>>(new Set());

  readonly filteredMenu = computed(() => {
    const user = this.userService.currentUserPermission();
    const menu = this._menuConfig();
    const hidden = this.hiddenMenuIds();

    // Se non ho ancora l'userType, non filtro per permessi
    let filtered = user == null ? menu : this.filterMenuByPermissions(menu, user);
    // Applica filtro operatore se presente
    if (hidden.size > 0) {
      filtered = this.filterMenuByOperatorConfig(filtered, hidden);
    }
    return filtered;
  });

  constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly operatorViewsConfigService: OperatorViewsConfigService
  ) {
    // Crea un segnale reattivo per l'URL corrente che emette su ogni NavigationEnd
    const currentUrlSig = toSignal(
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(e => e.urlAfterRedirects),
        startWith(this.router.url)
      )
    );

    // Aggiorna lo stato "active" e "expanded" quando cambia la rotta
    effect(() => {
      const currentUrl = currentUrlSig();
      this._menuConfig.update(menu => this.updateActiveState(menu, currentUrl));
    });

    // Carica la config delle viste dell'operatore quando necessario
    effect(() => {
      const user = this.userService.currentUser();
      if (!user) {
        this.hiddenMenuIds.set(new Set());
        return;
      }
      if (user.livelloAccesso === 1) {
        // livelloAccesso === 1 => operatore
        this.operatorViewsConfigService.getOperatorViewsConfig().pipe(take(1)).subscribe((res) => {
          if (!res) {
            this.hiddenMenuIds.set(new Set());
            return;
          }
          const hidden = new Set<string>(res.filter(i => i && !i.canView).map(i => i.voceMenu));
          this.hiddenMenuIds.set(hidden);
        });
      }
      else {
        // Non operatore => nessun filtro aggiuntivo
        this.hiddenMenuIds.set(new Set());
      }
    });
  }

  // === PUBLIC METHODS ===

  /**
   * Apre/chiude un item, chiudendo gli altri allo stesso livello.
   * Se ha figli, non esegue navigazione.
   */
  toggleCategory(targetId: string): void {
    this._menuConfig.update(menu => this.toggleMenuItemRecursive(menu, targetId));
  }

  /**
   * Resetta tutto il menu (chiude tutte le sezioni)
   */
  collapseAll(): void {
    this._menuConfig.update(menu => this.deepMap(menu, item => ({...item, isExpanded: false})));
  }

  private generalNotificationsService = inject(GeneralNotificationsService);
  private hasPhase4 = toSignal(this.generalNotificationsService.notifications$.pipe(
    map(notes => notes.some(n => n.categoria === 'canone' && n.fase >= 4))
  ));

  navigateTo(item: MenuItem): void {
    // Se siamo in fase 4, permettiamo solo la navigazione alla pagina del canone
    if (this.hasPhase4()) {
      if (item.route === '/studio/licenza/canone') {
        this.router.navigate([item.route]);
      }
      return;
    }

    if (item.children?.length) {
      this.toggleCategory(item.id);
    }
    else if (item.route) {
      this.router.navigate([item.route]);
    }
  }

  /**
   * True se l'utente corrente può aprire la pagina all'indirizzo `url`, con
   * le stesse regole con cui il menu ne mostra la voce: il tipo di utente
   * (`permissions`) e, per gli operatori, le viste nascoste dallo studio.
   * Vale anche per le voci "genitore": una sezione nascosta nasconde tutte le
   * pagine che contiene.
   *
   * Le pagine che non compaiono nel menu (es. le notifiche) non hanno
   * permessi propri: basta essere autenticati.
   *
   * Non legge `filteredMenu`, perché subito dopo il login utente e viste
   * dell'operatore possono non essere ancora arrivati: li chiede e li attende.
   */
  async puoAprire(url: string): Promise<boolean> {
    await this.userService.fetchUserInfo();
    const user = this.userService.currentUser();
    if (!user) return false;

    const percorso = url.split(/[?#]/)[0];
    const catena = this.catenaVociPerRoute(this._menuConfig(), percorso);
    if (!catena) return true;

    const userType = user.userType;
    if (userType != null && catena.some(v => v.permissions && !v.permissions.includes(userType))) {
      return false;
    }

    // livelloAccesso === 1 => operatore, come nel caricamento delle viste nascoste.
    if (user.livelloAccesso === 1) {
      const config = await firstValueFrom(this.operatorViewsConfigService.getOperatorViewsConfig());
      const nascoste = new Set((config ?? []).filter(i => i && !i.canView).map(i => i.voceMenu));
      if (catena.some(v => nascoste.has(v.id))) return false;
    }

    return true;
  }

  /**
   * Voce del menu con la rotta indicata, preceduta dalle voci che la
   * contengono; `null` se nessuna voce ha quella rotta.
   */
  private catenaVociPerRoute(items: MenuItem[], route: string): MenuItem[] | null {
    for (const item of items) {
      if (item.route === route) return [item];
      const dentro = item.children ? this.catenaVociPerRoute(item.children, route) : null;
      if (dentro) return [item, ...dentro];
    }
    return null;
  }

  /**
   * Restituisce il menu completo (utile per debug o log)
   */
  getMenuSnapshot(): MenuItem[] {
    return structuredClone(this._menuConfig());
  }

  // === PRIVATE HELPERS ===

  /** Filtra in base ai permessi utente */
  private filterMenuByPermissions(items: MenuItem[], userType: UserType): MenuItem[] {
    return items
    .filter(item => !item.permissions || item.permissions.includes(userType))
    .map(item => ({
      ...item,
      children: item.children ? this.filterMenuByPermissions(item.children, userType) : undefined,
    }));
  }

  /** Ricorsione per aggiornare isExpanded in modo controllato */
  private toggleMenuItemRecursive(items: MenuItem[], targetId: string): MenuItem[] {
    return items.map(item => {
      const isTarget = item.id === targetId;
      const children = item.children ? this.toggleMenuItemRecursive(item.children, targetId) : item.children;
      const isChildTarget = this.findById(children, targetId);

      return {
        ...item,
        isExpanded: isTarget
          ? !item.isExpanded
          : !!isChildTarget,
        children,
      };
    });
  }

  /** Ricorsione per aggiornare lo stato "active" e "expanded" basandosi sulla rotta */
  private updateActiveState(items: MenuItem[], currentUrl?: string): MenuItem[] {
    return items.map(item => {
      const isActive = item.route ? currentUrl?.startsWith(item.route) : false;
      const children = item.children ? this.updateActiveState(item.children, currentUrl) : undefined;
      const hasActiveChild = !!children?.some(child => child.isActive);

      return {
        ...item,
        isActive: isActive || hasActiveChild,
        isActiveParent: hasActiveChild,
        isExpanded: hasActiveChild ? true : item.isExpanded,
        children,
      };
    });
  }

  /** Ricerca ricorsiva per id */
  private findById(items: MenuItem[] | undefined, id: string): MenuItem | undefined {
    if (!items) return undefined;
    for (const item of items) {
      if (item.id === id) return item;
      const found = this.findById(item.children, id);
      if (found) return found;
    }
    return undefined;
  }

  /** Utility per mappare ricorsivamente una struttura immutabile */
  private deepMap(items: MenuItem[], mapper: (item: MenuItem) => MenuItem): MenuItem[] {
    return items.map(item => ({
      ...mapper(item),
      children: item.children ? this.deepMap(item.children, mapper) : undefined,
    }));
  }

  /** Filtra ulteriormente il menu in base alla configurazione operatore (ids da nascondere) */
  private filterMenuByOperatorConfig(items: MenuItem[], hiddenIds: Set<string>): MenuItem[] {
    return items
    .filter(item => !hiddenIds.has(item.id))
    .map(item => {
      const filteredChildren = item.children ? this.filterMenuByOperatorConfig(item.children, hiddenIds) : undefined;
      return {
        ...item,
        children: filteredChildren && filteredChildren.length > 0 ? filteredChildren : undefined,
      };
    });
  }
}
